import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Stack,
  Paper,
  Avatar,
  Typography,
  CircularProgress,
  Pagination
} from '@material-ui/core';
import axios from 'axios';
import { CallReceived } from '@material-ui/icons';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import moment from 'moment';
import Scrollbar from 'src/components/Scrollbar';

const ROWS_PER_PAGE = 6;

function transferTypeLabel(status) {
  if (Number(status) === 2) return 'Received';
  if (Number(status) === 1) return 'Sent';
  return '—';
}

/** Returns image URL for Avatar, or undefined to use letter fallback */
function getProfilePicSrc(profilePic) {
  if (profilePic == null) return undefined;
  const s = String(profilePic).trim();
  if (!s) return undefined;
  const file = s.split('/').pop().split('?')[0].toLowerCase();
  if (file === 'avatar.jpg' || file === 'default.jpg') return undefined;
  return s;
}

export default function TransferHistory({ prodId }) {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(
    async (pageIndex) => {
      if (!prodId) {
        setRows([]);
        setTotalCount(0);
        return;
      }
      setLoading(true);
      try {
        const apiPage = pageIndex + 1;
        const res = await axios.post(`${REST_API_END_POINT}product/get-material-transfer-logs`, {
          material_id: prodId,
          page: apiPage,
          pageSize: ROWS_PER_PAGE
        });
        if (res.data.status === 1) {
          setRows(res.data.result || []);
          setTotalCount(Number(res.data.totalCount ?? 0));
        } else {
          setRows([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.log('transfer logs error', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [prodId]
  );

  useEffect(() => {
    setPage(0);
  }, [prodId]);

  useEffect(() => {
    fetchLogs(page);
  }, [fetchLogs, page]);

  const pageCount = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  return (
    <Card
      variant="outlined"
      className="sw-request-tab-updates"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: '450px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {loading && rows.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%' }}>
            <Scrollbar sx={{ width: '100%', height: '100%' }}>
              <Stack spacing={1.5} sx={{ p: 3, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                {!loading && rows.length === 0 && (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                    No transfer history for this material.
                  </Typography>
                )}
                {(rows || []).map((row) => {
                  const isReceived = Number(row.status) === 2;
                  const displayName = isReceived
                    ? 'Incoming transfer'
                    : row.userName?.trim() || 'User';
                  const initial = displayName.charAt(0).toUpperCase();
                  const timeStr =
                    row.timestamp != null
                      ? moment.unix(row.timestamp).format('DD-MM-YYYY HH:mm')
                      : '—';
                  const typeStr = transferTypeLabel(row.status);
                  const picSrc = !isReceived ? getProfilePicSrc(row.profile_pic) : undefined;

                  return (
                    <Stack
                      key={row.id}
                      direction="row"
                      spacing={2}
                      sx={{ width: '100%', minWidth: 0, alignItems: 'flex-start' }}
                    >
                      {isReceived ? (
                        <Avatar
                          alt={displayName}
                          sx={{
                            flexShrink: 0,
                            bgcolor: 'action.selected',
                            color: 'primary.main'
                          }}
                        >
                          <CallReceived style={{ fontSize: 22 }} />
                        </Avatar>
                      ) : (
                        <Avatar
                          alt={displayName}
                          src={picSrc}
                          sx={{ flexShrink: 0 }}
                          imgProps={{
                            onError: (e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '';
                            }
                          }}
                        >
                          {initial}
                        </Avatar>
                      )}
                      <Paper
                        sx={{
                          p: 1.5,
                          flex: '1 1 auto',
                          minWidth: 0,
                          maxWidth: '100%',
                          bgcolor: 'background.neutral'
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                            {displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'primary.main' }}>
                            {timeStr}
                            {typeStr !== '—' ? ` · ${typeStr}` : ''}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {row.message || '—'}
                        </Typography>
                      </Paper>
                    </Stack>
                  );
                })}
              </Stack>
            </Scrollbar>
          </Box>

          {totalCount > 0 && pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: 2,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`
              }}
            >
              {loading && rows.length > 0 && (
                <CircularProgress size={22} thickness={4} color="primary" />
              )}
              <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(e, value) => setPage(value - 1)}
                color="primary"
                size="small"
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </Card>
  );
}
